import React, { useCallback, useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, API, fileUrl } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import {
  UploadCloud,
  FileText,
  Image as ImageIcon,
  X,
  Sparkles,
  Loader2,
  ArrowRight,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";

// Friendly labels for every stage the backend reports while extracting.
// The user can see exactly where their PDF is in the pipeline.
// NOTE (Feb 2026): The pipeline now marks status=completed AFTER ai_suggestions/saving.
// Page snapshots + Perplexity research run in PARALLEL in the background — they're shown
// as a separate "enriching" pill on the edit page, not as blocking stages here.
const STAGE_STEPS = [
  { key: "queued", label: "Queued for AI" },
  { key: "reading_files", label: "Reading your PDF" },
  { key: "ai_extracting", label: "AI is extracting product details" },
  { key: "ai_suggestions", label: "Generating AI suggestions" },
  { key: "saving_results", label: "Saving results" },
];

export default function AdminProductUpload() {
  const [docs, setDocs] = useState([]);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [creating, setCreating] = useState(false);
  // While the AI runs we hold the in-flight product here and poll it.
  const [pending, setPending] = useState(null); // {id, extraction_status, extraction_stage, extraction_error, name}
  const nav = useNavigate();

  const uploadFiles = async (filelist, kind) => {
    if (!filelist?.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of filelist) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("kind", kind);
        const token = localStorage.getItem("heda_token");
        const res = await fetch(`${API}/upload`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        if (!res.ok) {
          const err = await res.text();
          throw new Error(err);
        }
        const data = await res.json();
        uploaded.push(data);
      }
      if (kind === "image") setImages((x) => [...x, ...uploaded]);
      else setDocs((x) => [...x, ...uploaded]);
    } catch (e) {
      alert(`Upload failed: ${e.message}`);
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!docs.length && !images.length) {
      alert("Please upload at least one document or image first.");
      return;
    }
    setCreating(true);
    try {
      const { data: product } = await api.post("/products", {
        file_ids: docs.map((f) => f.id),
        image_ids: images.map((f) => f.id),
      });
      try {
        // Returns immediately — extraction runs in background.
        await api.post(`/products/${product.id}/extract`);
      } catch (e) {
        console.error("Extraction kickoff error:", e);
      }
      // Switch the page to the live-progress view. We DO NOT navigate away.
      setPending({
        id: product.id,
        extraction_status: "processing",
        extraction_stage: "queued",
        extraction_error: null,
        name: null,
      });
    } catch (e) {
      alert(`Failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setCreating(false);
    }
  };

  // Poll the product while extraction is in progress.
  useEffect(() => {
    if (!pending?.id) return;
    if (pending.extraction_status !== "processing" && pending.extraction_status !== "pending") {
      return;
    }
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/products/${pending.id}`);
        if (cancelled) return;
        setPending((prev) =>
          prev && prev.id === data.id
            ? {
                ...prev,
                extraction_status: data.extraction_status,
                extraction_stage: data.extraction_stage,
                extraction_error: data.extraction_error,
                name: data.name,
              }
            : prev,
        );
        if (data.extraction_status === "completed") {
          // Navigate ONLY when everything is ready. The edit page will now load
          // with all the populated fields instead of a blank "Untitled product".
          setTimeout(() => nav(`/admin/products/${data.id}/edit`), 600);
        }
      } catch {
        // Ignore transient errors; keep polling.
      }
    };
    tick();
    const handle = setInterval(tick, 3000);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [pending?.id, pending?.extraction_status, nav]);

  // ============================================================
  // PROGRESS VIEW (shown after upload, replaces the dropzones)
  // ============================================================
  if (pending) {
    return (
      <AdminLayout>
        <ExtractionProgress
          pending={pending}
          onOpenAnyway={() => nav(`/admin/products/${pending.id}/edit`)}
          onRetry={async () => {
            setPending((p) => ({
              ...p,
              extraction_status: "processing",
              extraction_stage: "queued",
              extraction_error: null,
            }));
            try {
              await api.post(`/products/${pending.id}/extract`);
            } catch (e) {
              alert(e?.response?.data?.detail || "Could not restart extraction");
            }
          }}
          onCancel={() => {
            // Lets the user upload another product. The current draft stays in DB
            // and is reachable from Products list.
            setPending(null);
            setDocs([]);
            setImages([]);
          }}
        />
      </AdminLayout>
    );
  }

  // ============================================================
  // UPLOAD VIEW (default)
  // ============================================================
  return (
    <AdminLayout>
      <div className="mb-10">
        <div className="hed-overline">New product</div>
        <h1 className="font-serif text-4xl md:text-5xl mt-2 tracking-tight">
          Upload. Let AI do the typing.
        </h1>
        <p className="text-sm text-[#5C5C54] mt-2 max-w-2xl">
          Drop brochures, technical sheets, catalog pages and product images.
          Hedaply AI extracts a complete product profile you can edit before publishing.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Dropzone
          label="Documents"
          accept=".pdf,application/pdf"
          icon={FileText}
          help="PDF brochures, tech sheets, catalogs"
          items={docs}
          onDrop={(f) => uploadFiles(f, "document")}
          onRemove={(idx) => setDocs((x) => x.filter((_, i) => i !== idx))}
          uploading={uploading}
          testIdPrefix="doc"
        />
        <Dropzone
          label="Product Images"
          accept="image/*"
          icon={ImageIcon}
          help="JPG, PNG, WEBP — product photos"
          items={images}
          onDrop={(f) => uploadFiles(f, "image")}
          onRemove={(idx) => setImages((x) => x.filter((_, i) => i !== idx))}
          uploading={uploading}
          testIdPrefix="img"
          showPreview
        />
      </div>

      {/* AI extraction CTA */}
      <div className="mt-10 hed-card p-6 md:p-8 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-[#B5953B] text-white flex items-center justify-center rounded-sm">
            <Sparkles size={18} strokeWidth={1.5} />
          </div>
          <div>
            <div className="font-serif text-2xl">Run AI extraction</div>
            <p className="text-sm text-[#5C5C54] mt-1 max-w-md">
              Hedaply AI will read every page, identify the product, and structure all data.
              You&rsquo;ll see live progress here — no blank pages, no guessing.
            </p>
          </div>
        </div>
        <button
          data-testid="run-extraction-button"
          onClick={submit}
          disabled={creating || uploading}
          className="bg-[#1A1A18] hover:bg-[#2a2a26] disabled:opacity-50 text-white px-6 py-3 rounded-sm font-medium inline-flex items-center gap-2 transition-colors"
        >
          {creating && <Loader2 size={16} className="animate-spin" />}
          {creating ? "Creating draft..." : "Extract & Continue"}
          {!creating && <ArrowRight size={14} strokeWidth={1.5} />}
        </button>
      </div>
    </AdminLayout>
  );
}

// ============================================================
// ExtractionProgress — Live status while AI runs in background.
// ============================================================
function ExtractionProgress({ pending, onOpenAnyway, onRetry, onCancel }) {
  const isFailed = pending.extraction_status === "failed";
  const currentStage = pending.extraction_stage || "queued";
  const currentIdx = STAGE_STEPS.findIndex((s) => s.key === currentStage);

  return (
    <div className="max-w-2xl mx-auto py-8">
      <div className="hed-overline">New product · AI extraction in progress</div>
      <h1 className="font-serif text-3xl md:text-4xl mt-2 tracking-tight">
        {isFailed
          ? "Something went wrong."
          : "Hedaply AI is reading your brochure."}
      </h1>
      <p className="text-sm text-[#5C5C54] mt-3 max-w-xl leading-relaxed">
        {isFailed
          ? "Don't worry — your draft is saved. You can retry, or open the draft and fill things manually."
          : "This usually takes 30–90 seconds depending on the file. You can leave this tab open — we&rsquo;ll take you to the finished product as soon as it&rsquo;s ready."}
      </p>

      <div
        className="hed-card mt-8 p-6 md:p-8"
        data-testid="extraction-progress-card"
      >
        {/* Stage list */}
        <ul className="space-y-3">
          {STAGE_STEPS.map((step, idx) => {
            const done = !isFailed && currentIdx > idx;
            const active = !isFailed && currentIdx === idx;
            return (
              <li
                key={step.key}
                className="flex items-center gap-3"
                data-testid={`stage-${step.key}`}
              >
                <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  {done ? (
                    <CheckCircle2
                      size={16}
                      strokeWidth={2}
                      className="text-[#3A5A40]"
                    />
                  ) : active ? (
                    <Loader2
                      size={14}
                      strokeWidth={2}
                      className="animate-spin text-[#B5953B]"
                    />
                  ) : isFailed ? (
                    <span className="w-2 h-2 rounded-full bg-[#E8E6DF]" />
                  ) : (
                    <span className="w-2 h-2 rounded-full bg-[#E8E6DF]" />
                  )}
                </span>
                <span
                  className={`text-sm ${
                    done
                      ? "text-[#5C5C54] line-through opacity-70"
                      : active
                      ? "text-[#1A1A18] font-medium"
                      : "text-[#5C5C54]"
                  }`}
                >
                  {step.label}
                  {active && "…"}
                </span>
              </li>
            );
          })}
        </ul>

        {/* Failure banner */}
        {isFailed && (
          <div
            className="mt-6 bg-[#9E2A2B]/10 border border-[#9E2A2B]/30 text-[#9E2A2B] text-sm rounded-sm p-4"
            data-testid="upload-extraction-error"
          >
            <div className="flex items-start gap-2">
              <AlertTriangle size={16} strokeWidth={2} className="mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-semibold mb-1">AI extraction failed</div>
                <div className="font-mono text-[12px] leading-relaxed break-words">
                  {pending.extraction_error || "Unknown error"}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mt-6 flex items-center gap-3 flex-wrap">
          {isFailed ? (
            <>
              <button
                onClick={onRetry}
                data-testid="extraction-retry-button"
                className="bg-[#B5953B] hover:bg-[#9A7E32] text-white px-5 py-2.5 rounded-sm text-sm font-semibold inline-flex items-center gap-2 transition-colors"
              >
                <Sparkles size={14} strokeWidth={2} /> Try again
              </button>
              <button
                onClick={onOpenAnyway}
                data-testid="extraction-open-anyway"
                className="border border-[#E8E6DF] hover:bg-[#FAFAF8] text-[#1A1A18] px-5 py-2.5 rounded-sm text-sm transition-colors"
              >
                Open draft anyway
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onOpenAnyway}
                data-testid="extraction-open-anyway"
                className="border border-[#E8E6DF] hover:bg-[#FAFAF8] text-[#1A1A18] px-5 py-2.5 rounded-sm text-sm transition-colors"
              >
                Open draft anyway
              </button>
              <button
                onClick={onCancel}
                data-testid="extraction-upload-another"
                className="text-[#5C5C54] hover:text-[#1A1A18] text-sm transition-colors"
              >
                Upload another product
              </button>
            </>
          )}
        </div>
      </div>

      {pending.name && !isFailed && (
        <div className="mt-6 text-sm text-[#5E6033] flex items-center gap-2">
          <Sparkles size={14} strokeWidth={2} />
          Identified as <strong>{pending.name}</strong>
        </div>
      )}
    </div>
  );
}

function Dropzone({ label, accept, icon: Icon, help, items, onDrop, onRemove, uploading, testIdPrefix, showPreview }) {
  const [drag, setDrag] = useState(false);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDrag(false);
      onDrop(Array.from(e.dataTransfer.files));
    },
    [onDrop],
  );

  return (
    <div className="hed-card p-6">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} strokeWidth={1.5} className="text-[#5E6033]" />
        <div className="hed-overline">{label}</div>
      </div>
      <label
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={handleDrop}
        className={`block border border-dashed cursor-pointer text-center py-12 px-4 rounded-sm transition-colors ${
          drag ? "border-[#B5953B] bg-[#F2F0EA]" : "border-[#E8E6DF] hover:bg-[#FAFAF8]"
        }`}
        data-testid={`${testIdPrefix}-dropzone`}
      >
        <input
          type="file"
          accept={accept}
          multiple
          className="hidden"
          onChange={(e) => onDrop(Array.from(e.target.files))}
          data-testid={`${testIdPrefix}-file-input`}
        />
        <UploadCloud size={28} strokeWidth={1.5} className="mx-auto text-[#5C5C54] mb-3" />
        <div className="text-sm font-medium">Drop files or click to upload</div>
        <div className="text-xs text-[#5C5C54] mt-1">{help}</div>
      </label>

      {uploading && <div className="text-xs text-[#5C5C54] mt-3">Uploading...</div>}

      {items.length > 0 && (
        <div className={`mt-4 ${showPreview ? "grid grid-cols-3 gap-3" : "space-y-2"}`}>
          {items.map((it, idx) =>
            showPreview ? (
              <div key={it.id} className="relative group" data-testid={`${testIdPrefix}-preview-${idx}`}>
                <img
                  src={fileUrl(it.storage_path)}
                  alt={it.original_filename}
                  className="w-full h-24 object-cover border border-[#E8E6DF] rounded-sm"
                />
                <button
                  type="button"
                  onClick={() => onRemove(idx)}
                  className="absolute top-1 right-1 bg-white/90 border border-[#E8E6DF] rounded-sm p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`${testIdPrefix}-remove-${idx}`}
                >
                  <X size={12} strokeWidth={2} />
                </button>
              </div>
            ) : (
              <div
                key={it.id}
                className="flex items-center justify-between text-sm px-3 py-2 bg-[#FAFAF8] border border-[#E8E6DF] rounded-sm"
                data-testid={`${testIdPrefix}-item-${idx}`}
              >
                <div className="truncate flex items-center gap-2">
                  <FileText size={14} strokeWidth={1.5} className="text-[#5E6033]" />
                  <span className="truncate">{it.original_filename}</span>
                </div>
                <button
                  type="button"
                  onClick={() => onRemove(idx)}
                  className="text-[#5C5C54] hover:text-[#9E2A2B] ml-2"
                  data-testid={`${testIdPrefix}-remove-${idx}`}
                >
                  <X size={14} strokeWidth={1.5} />
                </button>
              </div>
            ),
          )}
        </div>
      )}
    </div>
  );
}
